// recorder.ts — always-on video recording for adaptive offload
// (ARCHITECTURE.md §15.2). MediaRecorder starts on Start whatever the mode:
// it is hardware-encoded and cheap, and recording from the first frame means
// a later switch to server mode loses no footage.
//
//  * MIME preference: webm/vp9 → webm/vp8 → mp4/avc1 (iOS) → browser default.
//  * videoBitsPerSecond ≈ 8 Mbps at 1080p, scaled with the frame area.
//  * timeslice 1000 ms; every non-empty chunk gets the next sequence number.
//  * Until a sink is attached (device mode) chunks stay in a ChunkBuffer
//    capped at 200 MB; past the cap the oldest go and `truncated` is set.
//    attach() hands the backlog to the sink (the server uploader) in seq
//    order and streams every later chunk straight to it.
//  * tee() (device mode, §15.6) also hands every chunk — the current
//    buffer first — to the video archive, without draining the buffer.
//  * startMs is performance.now() at MediaRecorder.onstart — the clock the
//    IMU samples and the server's pts interleaving share.

export const MIME_PREFERENCE = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/mp4;codecs=avc1'] as const;
export const TIMESLICE_MS = 1000;
export const BUFFER_CAP_BYTES = 200 * 1024 * 1024;
export const BITRATE_1080P = 8_000_000;
const MIN_BITRATE = 1_000_000;
const MAX_BITRATE = 20_000_000;

export interface VideoChunk {
  seq: number;
  blob: Blob;
  tMs: number; // performance.now() when the chunk arrived
}

export type ChunkSink = (chunk: VideoChunk) => void;

// pickMime returns the first supported preference, or '' for the browser
// default.
export function pickMime(isTypeSupported: (mime: string) => boolean): string {
  for (const m of MIME_PREFERENCE) {
    try {
      if (isTypeSupported(m)) return m;
    } catch {
      // a throwing isTypeSupported means "no"
    }
  }
  return '';
}

// bitrateFor scales 8 Mbps @ 1920×1080 by frame area, clamped to 1–20 Mbps.
export function bitrateFor(width: number, height: number): number {
  if (!(width > 0 && height > 0)) return BITRATE_1080P;
  const b = Math.round((BITRATE_1080P * width * height) / (1920 * 1080));
  return Math.min(MAX_BITRATE, Math.max(MIN_BITRATE, b));
}

// ChunkBuffer keeps device-mode chunks in seq order under a byte cap,
// dropping the oldest (and flagging truncation) when it overflows.
export class ChunkBuffer {
  private chunks: VideoChunk[] = [];
  private size = 0;
  truncated = false;
  dropped = 0;

  constructor(readonly capBytes = BUFFER_CAP_BYTES) {}

  get bytes(): number {
    return this.size;
  }

  get length(): number {
    return this.chunks.length;
  }

  push(c: VideoChunk): void {
    this.chunks.push(c);
    this.size += c.blob.size;
    while (this.size > this.capBytes && this.chunks.length > 1) {
      const old = this.chunks.shift()!;
      this.size -= old.blob.size;
      this.dropped++;
      if (!this.truncated) console.warn(`[openscan/recorder] buffer over ${Math.round(this.capBytes / 1048576)} MB: dropping the oldest chunks (video_truncated)`);
      this.truncated = true;
    }
  }

  // peek returns the buffered chunks in seq order without removing them.
  peek(): VideoChunk[] {
    return this.chunks.slice();
  }

  // drain empties the buffer, returning its chunks in seq order.
  drain(): VideoChunk[] {
    const out = this.chunks;
    this.chunks = [];
    this.size = 0;
    return out;
  }
}

// RecorderLike is the slice of MediaRecorder this module uses (faked in tests).
export interface RecorderLike {
  readonly mimeType: string;
  readonly state: string;
  ondataavailable: ((ev: { data: Blob }) => void) | null;
  onstart: (() => void) | null;
  onstop: (() => void) | null;
  onerror: ((ev: unknown) => void) | null;
  start(timeslice?: number): void;
  stop(): void;
}

export interface RecorderFactory {
  isTypeSupported(mime: string): boolean;
  create(stream: MediaStream, opts: { mimeType?: string; videoBitsPerSecond: number }): RecorderLike;
}

// browserRecorderFactory wraps window.MediaRecorder, or null without one.
export function browserRecorderFactory(): RecorderFactory | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return {
    isTypeSupported: (m) => (typeof MediaRecorder.isTypeSupported === 'function' ? MediaRecorder.isTypeSupported(m) : false),
    create: (stream, opts) => new MediaRecorder(stream, opts) as unknown as RecorderLike,
  };
}

export class VideoRecorder {
  private rec: RecorderLike | null = null;
  private buffer: ChunkBuffer;
  private sink: ChunkSink | null = null;
  private teeSink: ChunkSink | null = null;
  private nextSeq = 0;
  private recorded = 0;
  private stopped: Promise<void> | null = null;
  private discarded = false;
  private resolveStopped: () => void = () => {};
  readonly requestedMime: string;
  readonly bitsPerSecond: number;
  startMs: number | null = null;
  onError: ((message: string) => void) | null = null;

  constructor(
    private stream: MediaStream,
    readonly width: number,
    readonly height: number,
    private factory: RecorderFactory | null = browserRecorderFactory(),
    capBytes = BUFFER_CAP_BYTES,
  ) {
    this.buffer = new ChunkBuffer(capBytes);
    this.requestedMime = factory ? pickMime((m) => factory.isTypeSupported(m)) : '';
    this.bitsPerSecond = bitrateFor(width, height);
  }

  get available(): boolean {
    return this.factory !== null;
  }

  get recording(): boolean {
    return this.rec !== null && this.rec.state !== 'inactive';
  }

  // mime is what the recorder actually produces (falls back to the request).
  get mime(): string {
    return this.rec?.mimeType || this.requestedMime;
  }

  get lastSeq(): number {
    return this.nextSeq - 1;
  }

  get chunkCount(): number {
    return this.nextSeq;
  }

  get bytesRecorded(): number {
    return this.recorded;
  }

  get truncated(): boolean {
    return this.buffer.truncated;
  }

  // start begins recording; false when MediaRecorder is missing or refuses.
  start(): boolean {
    if (!this.factory || this.rec) return false;
    try {
      const rec = this.factory.create(this.stream, { ...(this.requestedMime ? { mimeType: this.requestedMime } : {}), videoBitsPerSecond: this.bitsPerSecond });
      rec.onstart = () => {
        this.startMs = performance.now();
        console.info(`[openscan/recorder] recording ${rec.mimeType || '(default mime)'} ${this.width}x${this.height} @ ${(this.bitsPerSecond / 1e6).toFixed(1)} Mbps, start=${this.startMs.toFixed(1)} ms`);
      };
      rec.ondataavailable = (ev) => this.onData(ev.data);
      rec.onerror = (ev) => {
        const msg = `recorder error: ${String((ev as { error?: { message?: string } })?.error?.message ?? ev)}`;
        console.error(`[openscan/recorder] ${msg}`);
        this.onError?.(msg);
      };
      this.stopped = new Promise<void>((resolve) => (this.resolveStopped = resolve));
      rec.onstop = () => {
        console.info(`[openscan/recorder] stopped after ${this.nextSeq} chunks (${Math.round(this.recorded / 1024)} KiB)`);
        this.resolveStopped();
      };
      rec.start(TIMESLICE_MS);
      this.rec = rec;
      return true;
    } catch (err) {
      console.warn('[openscan/recorder] MediaRecorder could not start; server offload unavailable', err);
      this.rec = null;
      return false;
    }
  }

  private onData(blob: Blob): void {
    if (!blob || blob.size === 0 || this.discarded) return;
    const chunk: VideoChunk = { seq: this.nextSeq++, blob, tMs: performance.now() };
    this.recorded += blob.size;
    console.debug(`[openscan/recorder] chunk ${chunk.seq}: ${blob.size} B${this.sink ? ' → uploader' : ' (buffered)'}${this.teeSink ? ' + archive' : ''}`);
    if (this.sink) this.sink(chunk);
    else this.buffer.push(chunk);
    this.teeSink?.(chunk);
  }

  // tee hands every chunk to sink too (the §15.6 device-mode archive),
  // starting with what is buffered now, while the buffer keeps its copy for
  // a later attach() (switch to server mode). One tee at a time.
  tee(sink: ChunkSink | null): void {
    this.teeSink = sink;
    if (!sink) return;
    const backlog = this.buffer.peek();
    console.info(`[openscan/recorder] tee to the video archive: ${backlog.length} buffered chunks first`);
    backlog.forEach(sink);
  }

  // attach routes the buffered backlog and every later chunk to sink.
  attach(sink: ChunkSink): void {
    const backlog = this.buffer.drain();
    console.info(`[openscan/recorder] attaching uploader: ${backlog.length} buffered chunks${this.buffer.truncated ? ` (${this.buffer.dropped} dropped: truncated)` : ''}`);
    this.sink = sink;
    backlog.forEach(sink);
  }

  // stop ends the recording; resolves after the final chunk was delivered.
  stop(): Promise<void> {
    const rec = this.rec;
    if (!rec || rec.state === 'inactive') return this.stopped ?? Promise.resolve();
    try {
      rec.stop();
    } catch (err) {
      console.warn('[openscan/recorder] stop failed', err);
      this.resolveStopped();
    }
    return this.stopped ?? Promise.resolve();
  }

  // discard stops recording and frees the buffered chunks (page left).
  discard(): void {
    this.discarded = true;
    void this.stop();
    this.sink = null;
    this.teeSink = null;
    this.buffer.drain();
  }
}
